import * as d3 from "d3";
import * as React from "react";
import { PureComponent } from "react";
import { MeasuredComponentProps, withContentRect } from "react-measure";
import { useI18n } from "../domain/i18n";
import { dimValueDevelopment, ValueDevelopmentItem, Year } from "../domain/organization";

const formatPointLabel = d3.format(".2f");
const formatDeltaLabel = d3.format("+.2f");

interface CaptionProps {
  dim: any;
  isFirst: boolean;
}

const Caption = ({ dim, isFirst }: CaptionProps) => {
  const I18n = useI18n();
  return (
    <div className={"violin__caption key--" + dim.key} style={{ position: "relative", marginBottom: "10px" }}>
      <i className="violin__caption-icon" />
      <span className="violin__caption-texts">{dim.name}</span>
      {isFirst && <span className="violin__caption-legend">{I18n.t("frontend.detail.development.caption")}</span>}
    </div>
  );
};

const pointFor = (key: number, v: any, xScale: any, yScale: any, baseline: number, anchor: any) => {
  const xOffset = anchor === "start" ? 2 : anchor === "end" ? -2 : anchor === "middle" ? 0 : 0;
  const x = xScale(v.year);
  const y = yScale(v.value);
  const labelOffset = yScale(v.value) < baseline ? -10 : +22;

  return (
    <g key={key}>
      <circle cx={x + xOffset} cy={y} r={4} />
      <text style={{ textAnchor: anchor }} x={x - xOffset} y={y + labelOffset}>
        {formatPointLabel(v.value)}
      </text>
    </g>
  );
};

const deltaFor = (
  key: number,
  d: any,
  length: any,
  index: any,
  xScale: any,
  yScale: any,
  barWidth: any,
  anchor: any,
) => {
  const barWidth2 = barWidth / 2;
  const xOffset = anchor === "start" ? 2 : anchor === "end" ? -2 : anchor === "middle" ? 0 : 0;
  const x = xScale(d.year);
  const y = yScale(d.value);
  const baseY = yScale(d.base);
  const topY = d.delta > 0 ? yScale(d.base + d.delta) : baseY;
  const labelOffset = d.delta >= 0 ? -10 : +22;
  const lineX1Offset = index === 0 ? 0 : -barWidth2;
  const lineX2Offset = index === length - 1 ? 0 : barWidth2;

  return (
    <g key={key}>
      <line
        x1={xScale(d.prevYear) + lineX1Offset}
        y1={yScale(d.base)}
        x2={xScale(d.year) + lineX2Offset}
        y2={yScale(d.base)}
      />
      <rect
        x={x + lineX2Offset - barWidth}
        y={topY}
        width={barWidth}
        height={Math.abs(yScale(d.value) - yScale(d.base))}
      />
      <text style={{ textAnchor: anchor }} x={x - xOffset} y={y + labelOffset}>
        {formatDeltaLabel(d.delta)}
      </text>
    </g>
  );
};

export interface OrganizationDimensionDevelopmentChartProps {
  maxYDeviation: number;
  org: any;
  dim: any;
  xScale: any;
  isFirst: boolean;
}

interface Delta {
  prevYear: Year;
  year: Year;
  base: number;
  delta: number;
  value: number;
}

export class OrganizationDimensionDevelopmentChart extends PureComponent<
  OrganizationDimensionDevelopmentChartProps & { width: number }
> {
  render() {
    const { width, org, dim, maxYDeviation } = this.props;

    const height = 120;
    const bottomLegendHeight = 30;
    const barWidth = 20;
    const horizontalPadding = 20;

    const values = dimValueDevelopment(org, dim.key);
    const otherValues = values.slice(1);

    const xScale = this.props.xScale.range([horizontalPadding, width - horizontalPadding]);
    const yScale = d3.scale
      .linear()
      .domain([values[0].value - maxYDeviation, values[0].value + maxYDeviation])
      .range([height - 30, 30]);
    const baseline = yScale(values[0].value);

    const deltas = values.reduce<{ prev: null | ValueDevelopmentItem; v: Delta[] }>(
      (acc, v) => {
        const prev = acc.prev;
        if (prev === null) {
          return { prev: v, v: [] };
        } else {
          const delta: Delta = {
            prevYear: prev.year,
            year: v.year,
            base: prev.value,
            delta: v.value - prev.value,
            value: v.value,
          };
          return {
            prev: v,
            v: [...acc.v, delta],
          };
        }
      },
      { prev: null, v: [] },
    ).v;

    const areaPath = d3.svg
      .area<ValueDevelopmentItem>()
      .x((d) => xScale(d.year))
      .y0(baseline)
      .y1((d) => yScale(d.value));

    const ticks: string[] = org.availableYears;

    const x1 = xScale.domain()[0];
    const x2 = xScale.domain()[xScale.domain().length - 1];

    return (
      <div style={{ margin: "10px 0 30px" }}>
        <Caption dim={dim} isFirst={this.props.isFirst} />
        <svg
          className={"org-dim-dev-chart key--" + dim.key}
          width={width + "px"}
          height={height + bottomLegendHeight + "px"}
        >
          <line className="top-divider" x1={xScale(x1)} y1={1} x2={xScale(x2)} y2={1} />
          <path className="area" d={areaPath(values)} />
          <line
            className="center-line"
            x1={xScale(values[0].year)}
            y1={baseline}
            x2={xScale(values[values.length - 1].year)}
            y2={baseline}
          />
          {pointFor(0, values[0], xScale, yScale, baseline, "start")}
          <g className="other-points">
            {otherValues.map((x: any, i: any) =>
              pointFor(i, x, xScale, yScale, baseline, i === otherValues.length - 1 ? "end" : "middle"),
            )}
          </g>
          <g className="overlay">
            {deltas.map((d, i) =>
              deltaFor(
                i,
                d,
                deltas.length,
                i,
                xScale,
                yScale,
                barWidth,
                i === otherValues.length - 1 ? "end" : "middle",
              ),
            )}
          </g>
          <line className="bottom-divider" x1={xScale(x1)} y1={height} x2={xScale(x2)} y2={height} />
          {ticks.map((t, i) => {
            const x = xScale(t);
            const textAnchor = i === 0 ? "start" : i === ticks.length - 1 ? "end" : "middle";
            return (
              <text key={i} className={"tick"} style={{ textAnchor }} x={x} y={height + 18}>
                {t}
              </text>
            );
          })}
        </svg>
      </div>
    );
  }
}

const _OrganizationDimensionDevelopmentChart = (
  props: OrganizationDimensionDevelopmentChartProps & MeasuredComponentProps,
) => (
  <div ref={props.measureRef}>
    {props.contentRect.bounds && props.contentRect.bounds.width !== undefined && (
      <OrganizationDimensionDevelopmentChart {...props} width={props.contentRect.bounds.width} />
    )}
  </div>
);

export default withContentRect("bounds")(_OrganizationDimensionDevelopmentChart);
